import React, { useEffect } from 'react'
import { Form, schema } from '@ggs/forms'
import { useI18n } from '@ggs/i18n'
import { useValidators } from '@ggs/hooks'
import { Button } from '../../buttons'
import { useDrupalApi, useResolveResourceHook } from '../../../local'
import { addNotification, useDispatch, saveEntityOperation } from '@ggs/store'
import { get, noop, isArray } from 'lodash'
import { useModal } from '@ggs/ui/common'
import { Box } from '@mui/material'
import { CreateProjectForm, EditProjectForm } from '../../'
import { v4 as uuidv4 } from 'uuid'
import './SaveProjectResourceForm.scss'

/**
 * @typedef {Object} SaveProjectResourceFormProps
 * @property {string} resourceType Resource type (node bundle).
 * @property {string} resourceId Drupal Node ID of the resource that wants to be added.
 * @property {function=} closeModal Close modal handler, required when form displaying in a modal.
 */

/**
 * Save resource to existing project form.
 * @param {SaveProjectResourceFormProps} props
 * @return {JSX.Element}
 */
export function SaveProjectResourceForm({ resourceType, resourceId, closeModal = noop }) {
  const { t } = useI18n()
  const validators = useValidators()
  const operationId = uuidv4()
  const { getProject, getOwnProjects, addResourceToProject } = useDrupalApi()
  const { resolveResourceEntity, notifyResourceNotFound } = useResolveResourceHook({
    resourceType,
    resourceId,
  })
  const dispatch = useDispatch()
  const currentUserId = get(
    window || {},
    'drupalSettings.react.currentUserUUID',
    'a43e37ed-0f4f-4c75-a232-95c6f5bffbf3'
  )

  const { modal: CreateProjectModal, triggerModal: openCreateProjectModal } = useModal({
    scroll: 'body',
    className: 'create-project-dialog',
    title: (
      <Box className="create-project-dialog__title">
        <h2>{t('Create New Project')}</h2>
      </Box>
    ),
    content: (props) => {
      const closeCreateProjectModal = get(props, 'closeModal', noop)
      const closeModals = () => {
        closeCreateProjectModal()
        closeModal()
      }
      return <CreateProjectForm closeModal={closeModals} operationId={operationId} />
    },
  })

  const { modal: EditProjectModal, triggerModal: openEditProjectModal } = useModal({
    scroll: 'body',

    title: (
      <Box className="modal-title">
        <h2>{t('Edit New Project')}</h2>
      </Box>
    ),
    content: (props) => {
      const closeModal = get(props, 'closeModal', noop)
      return <EditProjectForm closeModal={closeModal} />
    },
  })

  /**
   * Abort add resource operation.
   */
  const abortOperation = () => {
    closeModal()
  }

  /**
   * Add into new project operation.
   */
  const addToNewProject = async () => {
    openCreateProjectModal()
    const resourceEntity = await resolveResourceEntity()
    dispatch(
      saveEntityOperation(operationId, 'add', {
        ...resourceEntity,
      })
    )
  }

  /**
   * Edit project operation.
   */
  const editProject = () => {
    // TODO: Discussed with Pierre, this operation can be removed, don't make sense within add resource flow.
    console.log('Handle Edit Project')
    openEditProjectModal()
  }

  /**
   * Handle resource add to project.
   * @param {Object<string, any>} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    const projectId = get(values, 'project', null)
    if (!projectId) {
      return
    }

    const resourceEntity = await resolveResourceEntity()
    if (!resourceEntity) {
      notifyResourceNotFound()
      setSubmitting(false)
      return
    }

    const projectNode = await getProject(projectId)
    if (!projectNode) {
      dispatch(
        addNotification(
          'project_not_found',
          'error',
          t('Project "#{{projectId}}" entity cannot be loaded.', {
            projectId: projectId,
          })
        )
      )

      setSubmitting(false)
      return
    }

    try {
      const updatedProjectNode = await addResourceToProject(projectNode, resourceEntity)
      if (updatedProjectNode) {
        dispatch(
          addNotification(
            'add_resource_success',
            'success',
            t('Resource "{{resourceName}}" was added to "{{projectName}}".', {
              resourceName: resourceEntity.title || resourceEntity.name,
              projectName: projectNode.title,
            })
          )
        )

        closeModal()
      } else {
        dispatch(
          addNotification(
            'add_resource_error',
            'error',
            t('Add resource "{{resourceName}}" to "{{projectName}}" failed.', {
              resourceName: resourceEntity.title || resourceEntity.name,
              projectName: projectNode.title,
            })
          )
        )
      }
    } catch (e) {
      console.log('IgnoredAddResourceError:', e)
    }

    setSubmitting(false)
  }

  const fieldsDefinition = {
    project: {
      label: t('Select Project'),
      type: schema.Forms.FieldTypes.select,
      validator: validators.nonBlank,
    },
  }

  const SaveProjectResourceForm = Form.useForm({
    withDraftState: true,
    formId: 'form_project_resource__add',
    className: 'form_project_resource__add',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        label: t('Add Project'),
        variant: Button.variant.contained,
        type: 'button',
        className: 'form__action-add',
        onClick: addToNewProject,
      },
      // {
      //   label: t('Edit Project'),
      //   variant: Button.variant.contained,
      //   type: 'button',
      //   className: 'form__action-edit',
      //   onClick: editProject,
      // },
      {
        label: t('Cancel'),
        variant: Button.variant.contained,
        type: 'button',
        className: 'form__action-cancel',
        onClick: abortOperation,
      },
      {
        type: 'submit',
        label: t('Save Selection'),
        className: 'form__action-submit',
        variant: Button.variant.contained,
      },
    ],
    sx: {},
    onSubmit: onSubmitHandler,
  })

  const resolveProjectOptions = async () => {
    const projectNodes = await getOwnProjects(currentUserId)

    if (!isArray(projectNodes)) {
      return
    }

    const projectOptions =
      projectNodes.length === 0
        ? [{ label: 'No projects available', value: '' }]
        : projectNodes.map((projectNode) => ({
            label: projectNode.title,
            value: `${projectNode.drupal_internal__nid}`,
          }))

    const newFields = {
      ...SaveProjectResourceForm.fields,
      project: {
        ...SaveProjectResourceForm.fields['project'],
        options: projectOptions,
      },
    }

    SaveProjectResourceForm.setFields(newFields)
  }

  // Resolve and compose project dropdown options.
  useEffect(() => {
    resolveProjectOptions().then(() => console.log('Resolved project options.'))
  }, [])

  return (
    <div className="add-project-resource">
      {CreateProjectModal}
      {EditProjectModal}
      {SaveProjectResourceForm.FormComp}
    </div>
  )
}
